import { apiClient } from './api.js';
import { unwrap } from './api-error.js';
import type { ApiPaths } from '@reos/api-client';

/**
 * Klient kalendáře makléře (krok CRM3). Typy jsou generované z
 * docs/api/openapi.json — UI si `canUpdate` a barvu typu jen ČTE, server je
 * počítá (viz `calendar-event-types.ts`).
 */

type Json = 'application/json';

export type CalendarEvent = NonNullable<
  ApiPaths['/v1/crm/calendar-events/{id}']['get']['responses']['200']['content'][Json]
>;

export type CalendarEventType = NonNullable<
  ApiPaths['/v1/crm/calendar-event-types']['get']['responses']['200']['content'][Json]
>['items'][number];

/** Klíč typu události — runtime registrovaný řetězec, proto ručně. */
export type CalendarEventTypeKey = CalendarEvent['eventType'];
export type CalendarEventStatus = CalendarEvent['status'];

export type CalendarReminderCandidate = NonNullable<
  ApiPaths['/v1/crm/calendar-reminders']['get']['responses']['200']['content'][Json]
>['items'][number];

export type CalendarReminderRunResult = NonNullable<
  ApiPaths['/v1/crm/calendar-reminders/run']['post']['responses']['200']['content'][Json]
>;

export type CalendarFeedStatus = NonNullable<
  ApiPaths['/v1/crm/calendar-feed']['get']['responses']['200']['content'][Json]
>;

export type CreateCalendarEventBody = ApiPaths['/v1/crm/calendar-events']['post']['requestBody']['content'][Json];
export type UpdateCalendarEventBody = ApiPaths['/v1/crm/calendar-events/{id}']['patch']['requestBody']['content'][Json];

export interface ListCalendarEventsParams {
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  readonly ownerUserId?: string | undefined;
  readonly eventType?: CalendarEventTypeKey | undefined;
  readonly limit?: number | undefined;
}

/** Jeden požadavek na obrazovku; další stránky se v UI necházejí. */
export async function listCalendarEvents(params: ListCalendarEventsParams = {}): Promise<CalendarEvent[]> {
  const result = await unwrap(
    apiClient.GET('/v1/crm/calendar-events', {
      params: {
        query: {
          limit: params.limit ?? 500,
          ...(params.from !== undefined ? { from: params.from } : {}),
          ...(params.to !== undefined ? { to: params.to } : {}),
          ...(params.ownerUserId !== undefined ? { ownerUserId: params.ownerUserId } : {}),
          ...(params.eventType !== undefined ? { eventType: params.eventType } : {}),
        },
      },
    }),
  );
  return result.items;
}

export async function listCalendarEventTypes(): Promise<CalendarEventType[]> {
  const result = await unwrap(apiClient.GET('/v1/crm/calendar-event-types', {}));
  return result.items;
}

export async function getCalendarEvent(id: string): Promise<CalendarEvent> {
  return unwrap(apiClient.GET('/v1/crm/calendar-events/{id}', { params: { path: { id } } }));
}

/**
 * Tělo UI skládá podmínně (`...(hodnota === '' ? {} : { pole: hodnota })`),
 * takže do něj tečou i `undefined`. Generovaný kontrakt z OpenAPI volitelné
 * položky deklaruje BEZ `| undefined` a v repu je zapnuté
 * `exactOptionalPropertyTypes` — převod je tady, na jednom místě.
 */
export async function createCalendarEvent(input: CreateCalendarEventBody): Promise<CalendarEvent> {
  return unwrap(apiClient.POST('/v1/crm/calendar-events', { body: input as CreateCalendarEventBody }));
}

/** Posunutí a natažení termínu jde přes tuto cestu (jen `startsAt`/`endsAt`). */
export async function updateCalendarEvent(
  id: string,
  input: UpdateCalendarEventBody,
): Promise<CalendarEvent> {
  return unwrap(apiClient.PATCH('/v1/crm/calendar-events/{id}', { params: { path: { id } }, body: input }));
}

export async function deleteCalendarEvent(id: string): Promise<void> {
  await unwrap(apiClient.DELETE('/v1/crm/calendar-events/{id}', { params: { path: { id } } }));
}

export async function listDueReminders(): Promise<CalendarReminderCandidate[]> {
  const result = await unwrap(apiClient.GET('/v1/crm/calendar-reminders', {}));
  return result.items;
}

export async function sendDueReminders(): Promise<CalendarReminderRunResult> {
  return unwrap(apiClient.POST('/v1/crm/calendar-reminders/run', { body: { locale: 'cs' } }));
}

export async function getCalendarFeedStatus(): Promise<CalendarFeedStatus> {
  return unwrap(apiClient.GET('/v1/crm/calendar-feed', {}));
}

/** Token se vrací JENOM tady; znovu ho zjistit nelze (v DB je jen hash). */
export async function rotateCalendarFeedToken(): Promise<string> {
  const result = await unwrap(apiClient.POST('/v1/crm/calendar-feed', {}));
  return result.token;
}

export async function revokeCalendarFeed(): Promise<void> {
  await unwrap(apiClient.DELETE('/v1/crm/calendar-feed', {}));
}

/** Odkaz na odběr pro Google Kalendar / Outlook. */
export function calendarFeedUrl(token: string): string {
  const base = typeof window === 'undefined' ? '' : window.location.origin;
  return `${base}/api/v1/crm/calendar-feed.ics?token=${encodeURIComponent(token)}`;
}

/** Odkaz na export iCal se stažením (s přihlášením v prohlížeči). */
export function calendarIcalUrl(): string {
  const base = typeof window === 'undefined' ? '' : window.location.origin;
  return `${base}/api/v1/crm/calendar-events.ics`;
}
